import React, { useEffect, useState } from 'react';
import { MessageSquarePlus, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Complaint } from '../../types';
import { formatDateTime } from '../../lib/format';
import {
  Spinner, EmptyState, Modal, Alert, StatusBadge,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';
import ReportFormModal from '../../components/ReportFormModal';

export default function Complaints() {
  const { user } = useAuth();
  const [roleTitle, setRoleTitle] = useState<string | null>(null);
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [residentModalOpen, setResidentModalOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmitResidents = user?.role === 'manager' || (roleTitle || '').toLowerCase().includes('facility');

  const loadComplaints = () => {
    setLoading(true);
    api
      .get<Complaint[]>('/complaints')
      .then(setComplaints)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadComplaints();
  }, []);

  useEffect(() => {
    api.get<{ profile?: { role_title?: string | null } }>('/auth/profile').then((me) => {
      setRoleTitle(me.profile?.role_title ?? null);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/complaints', { subject, description });
      setModalOpen(false);
      setSubject('');
      setDescription('');
      loadComplaints();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Complaints</h1>
        <div className="flex gap-2">
          <button onClick={() => { setError(''); setModalOpen(true); }} className={primaryBtn}>
            <MessageSquarePlus className="h-4 w-4" /> Submit Complaint
          </button>
          {canSubmitResidents && (
            <button onClick={() => { setError(''); setResidentModalOpen(true); }} className={primaryBtn}>
              <Users className="h-4 w-4" /> Report Resident Complaint
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Spinner label="Loading complaints..." />
      ) : complaints.length === 0 ? (
        <EmptyState message="You haven't submitted any complaints yet." />
      ) : (
        <div className="grid gap-4">
          {complaints.map((c) => (
            <div key={c.id} className="bg-white p-5 rounded-lg shadow-lg">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h3 className="font-semibold">{c.subject}</h3>
                <StatusBadge status={c.status} />
              </div>
              <p className="text-xs text-gray-500 mb-2">{formatDateTime(c.created_at)}</p>
              <p className="text-sm text-gray-600 whitespace-pre-line">{c.description}</p>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Submit a Personal Complaint">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Subject *</label>
            <input
              className={inputClass}
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your complaint"
            />
          </div>
          <div>
            <label className={labelClass}>Description *</label>
            <textarea
              className={inputClass}
              rows={6}
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your complaint in detail..."
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'Submitting...' : 'Submit Complaint'}
            </button>
          </div>
        </form>
      </Modal>

      <ReportFormModal
        open={residentModalOpen}
        onClose={() => setResidentModalOpen(false)}
        onSubmitted={() => {}}
        title="Report a Resident Complaint"
        defaultType="residents_complaint"
        allowedTypes={['residents_complaint']}
        showTypeSelect={false}
      />
    </div>
  );
}
